import React__default, { useRef, useState, useMemo } from 'react';
import { Root } from '@radix-ui/react-dropdown-menu';
import { ResizeObserver } from '@juggle/resize-observer';
import { MenuContextProvider } from './MenuContext.js';
import '../../hooks/index.js';
import useControllableState from '../../hooks/useControllableState/useControllableState.js';
import useEventListener from '../../hooks/useEventListener/useEventListener.js';

/* eslint-disable max-classes-per-file */
const Menu = _ref => {
  var _process$env;
  let {
    'data-testid': dataTestId,
    isOpen,
    onIsOpenChange,
    isDisabled,
    children,
    isVirtualizationDisabled
  } = _ref;
  if (typeof process !== 'undefined' && ((_process$env = process.env) === null || _process$env === void 0 ? void 0 : _process$env.NODE_ENV) === 'test' && window) {
    var _window$ResizeObserve, _window$PointerEvent;
    window.ResizeObserver = (_window$ResizeObserve = window.ResizeObserver) !== null && _window$ResizeObserve !== void 0 ? _window$ResizeObserve : ResizeObserver;
    window.PointerEvent = (_window$PointerEvent = window.PointerEvent) !== null && _window$PointerEvent !== void 0 ? _window$PointerEvent : PointerEvent;
  }
  const [open, setOpen] = useControllableState(isOpen, onIsOpenChange, isOpen);
  const virtuoso = useRef(null);
  const [maskHeight, setMaskHeight] = useState(null);
  const [maskWidth, setMaskWidth] = useState(null);
  const [submenuDepths, setSubmenuDepths] = useState({});
  const triggerRef = useRef(null);
  const registerSubmenu = (submenuId, submenuIsOpen, parentSubmenuId) => {
    setSubmenuDepths(subs => {
      const clone = {
        ...subs
      };
      if (submenuIsOpen) {
        const parentDepth = clone[parentSubmenuId];
        const submenuDepth = parentDepth ? parentDepth + 1 : 2;
        clone[submenuId] = submenuDepth;
      } else {
        delete clone[submenuId];
      }
      return clone;
    });
  };

  // Virtuoso and Radix-Dropdown have conflicting ResizeObserver behavior
  // according to the author of the error, this is not a problem https://stackoverflow.com/a/50387233/9487902
  // the virtuoso library author recommends this approach for conflicting libraries https://virtuoso.dev/react-beautiful-dnd/
  useEventListener(window, 'error', e => {
    if (e.message === 'ResizeObserver loop completed with undelivered notifications.' || e.message === 'ResizeObserver loop limit exceeded') {
      e.stopImmediatePropagation();
      e.preventDefault();
    }
  });
  const openPaneDepth = useMemo(() => {
    if (Object.values(submenuDepths).length) {
      return Math.max(...Object.values(submenuDepths));
    }
    if (open) {
      return 1;
    }
    return 0;
  }, [submenuDepths, open]);
  const [searchBarConfigs, setSearchBarConfigs] = useState([]);
  return /*#__PURE__*/React__default.createElement(MenuContextProvider, {
    value: {
      dataTestId,
      isDisabled,
      openPaneDepth,
      registerSubmenu,
      maskHeight,
      setMaskHeight,
      open,
      triggerRef,
      searchBarConfigs,
      setSearchBarConfigs,
      maskWidth,
      setMaskWidth,
      virtuoso,
      isVirtualizationDisabled,
      submenuDepths
    }
  }, /*#__PURE__*/React__default.createElement(Root, {
    open: open,
    onOpenChange: shouldOpen => setOpen(!isDisabled && shouldOpen),
    modal: false
  }, children));
};

// https://github.com/radix-ui/primitives/issues/1220#issuecomment-1087465026
class PointerEvent extends Event {
  constructor(type, props) {
    super(type, props);
    this.button = props.button || 0;
    this.ctrlKey = props.ctrlKey || false;
    this.pointerType = props.pointerType || 'mouse';
  }
}

export { Menu as default };
